import {
  get as getCookie,
  set as setCookie,
  remove as removeCookie,
} from "../js/utils/cookie/cookie";
import { EVENTS, COOKIES } from "../js/constants";

export default class DonationForm {
  /**
   * Creates a tracking instance that handles form events in donation form.
   */
  constructor() {
    this.config = {
      selectors: {
        donationForm: ".Tracking--donationForm",
        personalData: ".Tracking--donationFormPersonalData",
        selectedPaymentMethod:
          ".PaymentMethod .FskOptions--radios input:checked",
        selectedPaymentAmount:
          ".PaymentAmount-amount input[type='radio']:checked",
        selectedPaymentInterval:
          ".PaymentAmount-interval input[type='radio']:checked, .PaymentAmount-interval select",
        customPaymentAmount:
          ".PaymentAmount-amount input[name='spendenbetrag[donate_custom_amount]']",
      },
      attributes: {
        itemName: "data-item-name",
        itemId: "data-item-id",
      },
    };

    this.donationForm = document.querySelector(
      this.config.selectors.donationForm
    );

    this.donationFormId = this.donationForm?.getAttribute("id");

    this.personalData = document.querySelector(
      this.config.selectors.personalData
    );

    this._onFirstInput = this._onFirstInput.bind(this);
    this._onPersonalDataInput = this._onPersonalDataInput.bind(this);
    this._onPaymentSuccess = this._onPaymentSuccess.bind(this);

    if (this.donationForm && this.personalData) {
      this._bindFormEvents();
    }

    this._bindCookies();
  }

  /**
   * binds donation form events
   *
   * @private
   */
  _bindFormEvents() {
    this.donationForm.addEventListener("input", this._onFirstInput, {
      once: true,
    });
    this.personalData.addEventListener("input", this._onPersonalDataInput);

    this.donationForm.addEventListener(
      EVENTS.DONATION_FORM_PAYMENT_SUCCESS,
      this._onPaymentSuccess
    );

    this.donationForm.addEventListener(
      EVENTS.DONATION_FORM_PAYMENT_ERROR,
      DonationForm._onPaymentError
    );

    this.donationForm.addEventListener(
      EVENTS.DONATION_FORM_INPUT_ERROR,
      DonationForm._onInputError
    );
  }

  /**
   * handles the first donation form input no matter what form field it was
   * and pushes data to dataLayer
   *
   * @private
   */
  _onFirstInput() {
    const donationStart = getCookie(COOKIES.DONATION_START);

    if (!donationStart) {
      window.dataLayer.push({
        event: "Spende - Step 1 - Start",
        event_name: "begin_checkout",
        form_goal: "Donation",
        form_id: this.donationFormId,
        form_plugin: "Drupal",
        begin_checkout: {
          currency: "EUR",
          items: [this._getFormData()],
        },
      });

      setCookie(COOKIES.DONATION_START, 1, {
        path: "/",
        end: 60 * 15,
        SameSite: "Lax",
        secure: true,
      });
    }
  }

  /**
   * handles any input in personal data fieldset
   * and pushes data once to dataLayer when all inputs are valid in this fieldset
   *
   * @private
   */
  _onPersonalDataInput() {
    const invalidInputs = [
      ...this.personalData.querySelectorAll("input:invalid"),
    ];

    if (invalidInputs.length === 0) {
      window.dataLayer.push({
        event: "Spende - Step 2 - User Info",
        event_name: "add_shipping_info",
        form_goal: "Donation",
        form_id: this.donationFormId,
        form_plugin: "Drupal",
        add_shipping_info: {
          shipping_tier: "online",
          items: [this._getFormData()],
        },
      });

      this.personalData.removeEventListener("input", this._onPersonalDataInput);
    }
  }

  /**
   * handles payment success in donation form
   * and pushes data to dataLayer
   *
   * @private
   */
  _onPaymentSuccess() {
    window.dataLayer.push({
      event: "Spende - Step 3 - Payment Info",
      event_name: "add_payment_info",
      form_goal: "Donation",
      form_id: this.donationFormId,
      form_plugin: "Drupal",
      add_payment_info: {
        payment_type: this._getPaymentMethod(),
        items: [this._getFormData()],
      },
    });
  }

  /**
   * handles payment error in donation form
   *
   * @param {object} data
   * @private
   * @static
   */
  static _onPaymentError(data) {
    DonationForm._onError(data.error_type);
  }

  /**
   * handles invalid inputs in donation form
   *
   * @private
   * @static
   */
  static _onInputError() {
    DonationForm._onError("invalid inputs");
  }

  /**
   * handles errors in donation form
   * and pushes data to dataLayer
   *
   * @param {string} errorType
   * @private
   * @static
   */
  static _onError(errorType) {
    window.dataLayer.push({
      event: "Event",
      event_name: "donation_error",
      donation_error: {
        error_type: errorType,

        pp_event_category: "spende - error",
        pp_event_action: errorType,
        pp_event_name: undefined,
      },
    });
  }

  /**
   * returns all relevant donation form data
   *
   * @private
   * @returns {object}
   */
  _getFormData() {
    const data = {
      ...this._getPaymentIntervalAndAmount(),
      item_name: this.donationForm.getAttribute(
        this.config.attributes.itemName
      ),
      item_id: this.donationForm.getAttribute(this.config.attributes.itemId),
      item_list_name: getCookie(COOKIES.DONATION_ENTRY_POINT),
    };

    return DonationForm._prepareFormDataForTracking(data);
  }

  /**
   * gets payment interval and amount infos from donation form
   * and converts them into relevant information for tracking
   *
   * @private
   * @returns {object}
   */
  _getPaymentIntervalAndAmount() {
    let data;

    const interval = this.donationForm.querySelector(
      this.config.selectors.selectedPaymentInterval
    )?.value;

    const checkedRadioAmount = this.donationForm.querySelector(
      this.config.selectors.selectedPaymentAmount
    );

    const textAmount = this.donationForm.querySelector(
      this.config.selectors.customPaymentAmount
    );

    const amount = (checkedRadioAmount || textAmount)?.value;

    switch (interval) {
      case "12":
        data = {
          item_variant: "monatlich",
          price: amount * 12,
          item_category: "regelmäßige Spende",
        };
        break;
      case "4":
        data = {
          item_variant: "vierteljährlich",
          price: amount * 4,
          item_category: "regelmäßige Spende",
        };
        break;
      case "2":
        data = {
          item_variant: "halbjährlich",
          price: amount * 2,
          item_category: "regelmäßige Spende",
        };
        break;
      case "1":
        data = {
          item_variant: "jährlich",
          price: amount,
          item_category: "regelmäßige Spende",
        };
        break;
      default:
        data = {
          item_variant: "einmalig",
          price: amount,
          item_category: "einmalige Spende",
        };
        break;
    }

    return data;
  }

  /**
   * returns payment method from donation form
   *
   * @private
   * @returns {string}
   */
  _getPaymentMethod() {
    return this.donationForm.querySelector(
      this.config.selectors.selectedPaymentMethod
    ).value;
  }

  /**
   * brings form data into the right format for tracking
   * and adds some static values
   *
   * @param {object} data
   * @private
   * @static
   * @returns {object} items
   */
  static _prepareFormDataForTracking(data) {
    return {
      item_name: data.item_name,
      item_id: data.item_id,
      price: data.price,
      item_brand: "Greenpeace",
      item_category: data.item_category,
      item_variant: data.item_variant,
      item_list_name: data.item_list_name,
      index: 1,
      quantity: "1",
    };
  }

  /**
   * binds cookies with payment information
   * some information from previous pages have been stored to cookies
   *
   * @private
   * @static
   */
  _bindCookies() {
    const verification = getCookie(COOKIES.DONATION_VERIFICATION);
    const purchase = getCookie(COOKIES.DONATION_PURCHASE);

    if (verification) {
      this._onPaymentVerification();
    }

    if (purchase) {
      DonationForm._onPurchase(JSON.parse(purchase));
    }
  }

  /**
   * handles successful payment verification in donation form
   * pushes data to dataLayer and removes cookie
   * happens on "checkout" page
   *
   * @private
   */
  _onPaymentVerification() {
    window.dataLayer.push({
      event: "Spende - Step 4 - Verification",
      event_name: "checkout_verification",
      form_goal: "Donation",
      form_id: this.donationFormId,
      form_plugin: "Drupal",
    });

    removeCookie(COOKIES.DONATION_VERIFICATION, { path: "/" });
  }

  /**
   * handles successful purchase in donation form
   * pushes data to dataLayer and removes cookie
   * happens on "Thank you" page
   *
   * @param {object} data
   * @private
   * @static
   */
  static _onPurchase(data) {
    window.dataLayer.push({
      event: "Spende - Step 5 - Purchase",
      event_name: "purchase",
      user_id: data.user_id,
      form_goal: "Donation",
      form_id: data.form_id,
      form_plugin: "Drupal",
      form_contains_newsletter_subscription:
        data.form_contains_newsletter_subscription || false,
      form_contains_address_field: data.form_contains_address_field || false,
      form_contains_phone_field: data.form_contains_phone_field || false,
      phone_field_provided: data.phone_field_provided || false,
      newsletter_subscribed: data.newsletter_subscribed || false,
      purchase: {
        transaction_id: data.transaction_id,
        value: data.revenue,
        currency: "EUR",
        tax: "0.00",
        shipping: "0.00",
        items: [DonationForm._prepareFormDataForTracking(data)],
      },
    });

    removeCookie(COOKIES.DONATION_START, { path: "/" });
    removeCookie(COOKIES.DONATION_PURCHASE, { path: "/" });
  }
}
